// Asset preview — PARTS tab: every painted cutout part the game loads, grouped per character
// (mech rig parts + equipment, weapons, enemy and boss parts). The other tabs show the baked
// results (sheets, boss textures); this one shows the source pieces they are built from, so a
// missing or wrong part is visible at a glance. Setup-pose rigs (pieces sliced from a concept,
// rig.json "mode": "setup") also get an ASSEMBLED cell: every piece placed back at its crop
// origin, which must reproduce the concept exactly.
//
// Q / E (or the buttons) switch group, [ / ] flip pages.

import Phaser from 'phaser';
import { FOE_PARTS, foeKeys } from '../../art/foes';
import { CUTOUT_PARTS, EQUIP_PART, cutoutKeys, equipKeys } from '../../art/mech/cutout';
import { COLORS } from '../../config/palette';
import { STAGE_PIECES, stageKeys } from '../../art/world/stage';
import { BIOME_IDS, MECH_IDS, WEAPON_IDS, type EquipId } from '../../core/types';
import { Gallery, type GalleryEntry } from './gallery';
import type { PreviewTab } from './tab';
import { Button, PV } from './widgets';

interface Group {
  name: string;
  source: string;
  entries: () => GalleryEntry[];
}

const ACCENT = COLORS.volt;

function size(scene: Phaser.Scene, key: string): string | undefined {
  if (!scene.textures.exists(key)) return 'not loaded';
  const src = scene.textures.get(key).getSourceImage() as { width?: number; height?: number };
  return `${src?.width ?? '?'}×${src?.height ?? '?'}`;
}

/** Canvas texture with every setup-pose piece at its crop origin (null for skeleton rigs). */
function assembled(scene: Phaser.Scene, id: string): string | null {
  const json = scene.cache.json.get(foeKeys.rig(id)) as
    | { mode?: string; order?: string[]; pieces?: Record<string, { origin: [number, number] }> }
    | undefined;
  if (json?.mode !== 'setup' || !json.pieces) return null;
  const key = `pv_assembled_${id}`;
  if (scene.textures.exists(key)) return key;
  const names = [...(json.order ?? []), ...Object.keys(json.pieces).filter((n) => !json.order?.includes(n))];
  let w = 1;
  let h = 1;
  const imgs: { img: CanvasImageSource; x: number; y: number }[] = [];
  for (const n of names) {
    const k = foeKeys.part(id, n);
    const o = json.pieces[n]?.origin;
    if (!o || !scene.textures.exists(k)) continue;
    const img = scene.textures.get(k).getSourceImage() as HTMLImageElement;
    imgs.push({ img, x: o[0], y: o[1] });
    w = Math.max(w, o[0] + img.width);
    h = Math.max(h, o[1] + img.height);
  }
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) return null;
  for (const it of imgs) tex.context.drawImage(it.img, it.x, it.y);
  tex.refresh();
  return key;
}

function foeGroup(scene: Phaser.Scene, id: string): Group {
  return {
    name: id.toUpperCase(),
    source: `assets/foes/${id}/`,
    entries: () => {
      const out: GalleryEntry[] = [];
      const whole = assembled(scene, id);
      if (whole) out.push({ key: whole, label: 'ASSEMBLED', sub: 'pieces at their origins' });
      for (const p of FOE_PARTS[id]) {
        const key = foeKeys.part(id, p);
        out.push({ key, label: p, sub: size(scene, key) });
      }
      return out;
    },
  };
}

function groups(scene: Phaser.Scene): Group[] {
  const list: Group[] = MECH_IDS.map((id) => ({
    name: id.toUpperCase(),
    source: `assets/mech/${id}/ + equip/`,
    entries: () => [
      ...CUTOUT_PARTS.map((p) => {
        const key = cutoutKeys.part(id, p);
        return { key, label: p, sub: size(scene, key) };
      }),
      ...(Object.keys(EQUIP_PART) as EquipId[]).map((e) => {
        const key = equipKeys.img(id, e);
        return { key, label: e, sub: `${EQUIP_PART[e]} · ${size(scene, key)}` };
      }),
    ],
  }));
  list.push({
    name: 'WEAPONS',
    source: 'assets/weapons/',
    entries: () =>
      WEAPON_IDS.map((w) => {
        const key = cutoutKeys.weapon(w);
        return { key, label: w, sub: size(scene, key) };
      }),
  });
  for (const id of Object.keys(FOE_PARTS)) list.push(foeGroup(scene, id));
  // Painted stage objects (catwalk, hazard, strut, props) per biome.
  for (const b of BIOME_IDS) {
    list.push({
      name: b.toUpperCase(),
      source: `assets/stage/${b}/`,
      entries: () =>
        STAGE_PIECES.map((p) => {
          const key = stageKeys.piece(b, p);
          return { key, label: p, sub: size(scene, key) };
        }),
    });
  }
  return list;
}

export class PartsTab implements PreviewTab {
  readonly root: Phaser.GameObjects.Container;
  private gallery: Gallery;
  private groups: Group[];
  private buttons: Button[] = [];
  private index = 0;

  constructor(private scene: Phaser.Scene) {
    this.root = scene.add.container(0, 0);
    this.groups = groups(scene);
    const x = PV.pad;
    const w = PV.W - PV.pad * 2;
    const y = PV.headerH + 14;
    // Two rows of group buttons.
    const perRow = Math.ceil(this.groups.length / 2);
    const bw = Math.floor((w - (perRow - 1) * 6) / perRow);
    this.groups.forEach((g, i) => {
      const bx = x + (i % perRow) * (bw + 6);
      const by = y + Math.floor(i / perRow) * 40;
      const b = new Button(scene, bx, by, g.name, () => this.select(i), { w: bw, h: 34, toggle: true, size: 13, accent: ACCENT });
      this.buttons.push(b);
      this.root.add(b.root);
    });
    const gy = y + 88;
    this.gallery = new Gallery(scene, [], { x, y: gy, w, h: PV.H - PV.pad - gy, cellW: 210, cellH: 228, maxScale: 2, accent: ACCENT });
    this.root.add(this.gallery.root);
    this.select(0);
  }

  private select(i: number): void {
    this.index = (i + this.groups.length) % this.groups.length;
    this.buttons.forEach((b, j) => b.setActive(j === this.index));
    const g = this.groups[this.index];
    const entries = g.entries();
    const missing = entries.filter((e) => !this.scene.textures.exists(e.key)).length;
    this.gallery.setEntries(entries);
    this.gallery.setTitle(`${g.name} PARTS · ${g.source}${missing ? ` · ${missing} MISSING (procedural fallback)` : ''}`);
  }

  update(dt: number): void {
    this.gallery.update(dt);
  }

  key(code: string): boolean {
    if (code === 'KeyQ') this.select(this.index - 1);
    else if (code === 'KeyE') this.select(this.index + 1);
    else if (code === 'BracketRight' || code === 'PageDown') this.gallery.flip(1);
    else if (code === 'BracketLeft' || code === 'PageUp') this.gallery.flip(-1);
    else return false;
    return true;
  }

  destroy(): void {
    this.root.destroy();
  }
}
